import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiConfiguration } from '@/core/api/api-configuration';
import { BaseService } from '@/core/api/base-service';
import { environment } from '@env/environment';
import { SICOM_PATHS } from 'src/sicom-paths';
import { PageResponse, SasUsuarioSearchResponse, SolicitanteSearchResponse, UsuarioCriarCompletoRequest, UsuarioEstruturaRequest, UsuarioEstruturaResponse, UsuarioRequest, UsuarioResponse, UsuarioResumoResponse } from './usuario.model';

@Injectable({ providedIn: 'root' })
export class UsuarioService extends BaseService {
    private readonly BASE = SICOM_PATHS.sistema.usuarios.base;
    private readonly BUSCAR_SAS = SICOM_PATHS.sistema.usuarios.buscarSas;
    private readonly KEYCLOAK_ID = SICOM_PATHS.sistema.usuarios.keycloakId;
    private readonly KEYCLOAK_USER = SICOM_PATHS.sistema.usuarios.keycloakUser;
    private readonly SOLICITANTE_EMAILS = SICOM_PATHS.sistema.usuarios.solicitanteEmails;

    constructor(config: ApiConfiguration, http: HttpClient) {
        super(config, http, environment.apis.apiSicom);
    }

    listar(params: { nome?: string; cpf?: string; uf?: string; userId?: string; inAtivo?: string; page?: number; size?: number }): Observable<PageResponse<UsuarioResumoResponse>> {
        let httpParams = new HttpParams();
        if (params.nome) httpParams = httpParams.set('nome', params.nome);
        if (params.cpf) httpParams = httpParams.set('cpf', params.cpf);
        if (params.uf) httpParams = httpParams.set('uf', params.uf);
        if (params.userId) httpParams = httpParams.set('userId', params.userId);
        if (params.inAtivo) httpParams = httpParams.set('inAtivo', params.inAtivo);
        httpParams = httpParams.set('page', params.page ?? 0);
        httpParams = httpParams.set('size', params.size ?? 50);
        return this.http.get<PageResponse<UsuarioResumoResponse>>(this.BASE, { params: httpParams });
    }

    buscarPorId(id: number): Observable<UsuarioResponse> {
        return this.http.get<UsuarioResponse>(`${this.BASE}/${id}`);
    }

    criar(request: UsuarioRequest): Observable<UsuarioResponse> {
        return this.http.post<UsuarioResponse>(this.BASE, request);
    }

    criarCompleto(request: UsuarioCriarCompletoRequest): Observable<UsuarioResponse> {
        return this.http.post<UsuarioResponse>(SICOM_PATHS.sistema.usuarios.criarCompleto, request);
    }

    atualizar(id: number, request: UsuarioRequest): Observable<UsuarioResponse> {
        return this.http.put<UsuarioResponse>(`${this.BASE}/${id}`, request);
    }

    ativarInativar(id: number): Observable<UsuarioResponse> {
        return this.http.patch<UsuarioResponse>(`${this.BASE}/${id}/status`, {});
    }

    /** Corrige o CPF do usuário (propaga ao Keycloak: login + atributo cpf). */
    alterarCpf(id: number, cpf: string): Observable<UsuarioResponse> {
        return this.http.put<UsuarioResponse>(`${this.BASE}/${id}/cpf`, { cpf });
    }

    /** Atribui/remove o Agente de Registro. 422 quando o usuário não tem cadastro de funcionário. */
    alterarAgenteAgr(id: number, agenteAgr: boolean): Observable<void> {
        return this.http.put<void>(`${this.BASE}/${id}/agente-agr`, { agenteAgr });
    }

    buscarSas(q: string, page = 0, size = 10): Observable<PageResponse<SasUsuarioSearchResponse>> {
        const params = new HttpParams().set('q', q).set('page', page).set('size', size);
        return this.http.get<PageResponse<SasUsuarioSearchResponse>>(this.BUSCAR_SAS, { params });
    }

    buscarSolicitante(q: string, uf: string, page = 0, size = 15): Observable<PageResponse<SolicitanteSearchResponse>> {
        const params = new HttpParams().set('q', q).set('uf', uf).set('page', page).set('size', size);
        return this.http.get<PageResponse<SolicitanteSearchResponse>>(SICOM_PATHS.sistema.usuarios.buscarSolicitante, { params });
    }

    buscarUserIdKeycloak(idSolicitante: number, uf: string): Observable<{ userId: string; nome?: string; email?: string }> {
        return this.http.get<{ userId: string; nome?: string; email?: string }>(this.KEYCLOAK_ID, { params: new HttpParams().set('idSolicitante', idSolicitante).set('uf', uf) });
    }

    criarUsuarioKeycloak(idSolicitante: number, uf: string, email: string): Observable<{ userId: string }> {
        return this.http.post<{ userId: string }>(this.KEYCLOAK_USER, { idSolicitante, uf, email });
    }

    buscarUserIdKeycloakPorCpf(cpf: string): Observable<{ userId: string; nome?: string; email?: string }> {
        return this.http.get<{ userId: string; nome?: string; email?: string }>(this.KEYCLOAK_ID, { params: new HttpParams().set('cpf', cpf) });
    }

    criarUsuarioKeycloakPorCpf(cpf: string, nome: string, email: string): Observable<{ userId: string }> {
        return this.http.post<{ userId: string }>(this.KEYCLOAK_USER, { cpf, nome, email });
    }

    listarEmailsSolicitante(idSolicitante: number, uf: string): Observable<string[]> {
        return this.http.get<string[]>(this.SOLICITANTE_EMAILS, { params: new HttpParams().set('idSolicitante', idSolicitante).set('uf', uf) });
    }

    listarSetores(idUsuario: number): Observable<UsuarioEstruturaResponse[]> {
        return this.http.get<UsuarioEstruturaResponse[]>(`${this.BASE}/${idUsuario}/estruturas`);
    }

    adicionarSetor(idUsuario: number, request: UsuarioEstruturaRequest): Observable<UsuarioEstruturaResponse> {
        return this.http.post<UsuarioEstruturaResponse>(`${this.BASE}/${idUsuario}/estruturas`, request);
    }

    atualizarSetor(idUsuario: number, idEstrutura: number, request: UsuarioEstruturaRequest): Observable<UsuarioEstruturaResponse> {
        return this.http.put<UsuarioEstruturaResponse>(`${this.BASE}/${idUsuario}/estruturas/${idEstrutura}`, request);
    }

    removerSetor(idUsuario: number, idEstrutura: number): Observable<void> {
        return this.http.delete<void>(`${this.BASE}/${idUsuario}/estruturas/${idEstrutura}`);
    }
}
